import React, { useState, useEffect, useRef } from 'react';
import {
  MdSettings,
  MdVisibility,
  MdVisibilityOff,
  MdEdit,
  MdDescription,
  MdSave,
  MdCancel,
  MdInfo,
  MdDownload,
  MdUpload,
  MdContentCopy
} from 'react-icons/md';
import { useClass } from '../utils/ClassContext';
import { classApi } from '../utils/api/ClassApi';

const ClassSettings = ({ username, userRole, apiBaseUrl }) => {
  const { selectedClass } = useClass();
  const [settings, setSettings] = useState({
    documentEditorEnabled: true,
    erdEditorEnabled: true,
    className: selectedClass?.class_name || '',
    classCode: selectedClass?.class_code || '',
    description: selectedClass?.description || ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedSettings, setEditedSettings] = useState({ ...settings });
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Export/Import State
  const importFileRef = useRef(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  // Migration Wizard State
  const [showMigrationWizard, setShowMigrationWizard] = useState(false);
  const [migrationStep, setMigrationStep] = useState(1);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationError, setMigrationError] = useState(null);
  // Tracks which phase of the create+migrate flow is in progress, so the wizard
  // can show a real progress bar instead of a single static loading label.
  // One of: null | 'export' | 'create' | 'import' | 'done'
  const [migrationPhase, setMigrationPhase] = useState(null);
  const [newClassName, setNewClassName] = useState('');
  const [newClassCode, setNewClassCode] = useState('');
  const [newSemester, setNewSemester] = useState('');
  const [newYear, setNewYear] = useState(new Date().getFullYear().toString());
  const [selectedItems, setSelectedItems] = useState({
    environments: true,
    assignments: true,
    documents: true,  // New: standalone documents/sections
    rubrics: true,
    enrollments: false  // New: enrollments (independent)
  });

  // When true, migration copies every course-content collection in the class
  // database (including ones not covered by the checkboxes above, like ERD
  // requirements and attachments) and hard-excludes student-generated data
  // (submissions, grades, feedback, activity logs, enrollments, etc).
  const [copyAllExceptStudentData, setCopyAllExceptStudentData] = useState(false);

  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    publishedOnly: false,
    excludeArchived: true,
    includeStandalone: true  // New: include standalone (non-assignment-linked) content
  });

  // Canvas Integration State
  const [showCanvasModal, setShowCanvasModal] = useState(false);
  const [canvasStep, setCanvasStep] = useState(1);
  const [canvasLoading, setCanvasLoading] = useState(false);
  const [canvasError, setCanvasError] = useState(null);
  const [canvasUrl, setCanvasUrl] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [hasCanvasCredentials, setHasCanvasCredentials] = useState(false);
  const [canvasCourses, setCanvasCourses] = useState([]);
  const [selectedCanvasCourse, setSelectedCanvasCourse] = useState(null);
  const [canvasIntegration, setCanvasIntegration] = useState(null);

  // Student sync state
  const [showStudentSync, setShowStudentSync] = useState(false);
  const [studentPreview, setStudentPreview] = useState(null);
  const [studentSyncLoading, setStudentSyncLoading] = useState(false);
  const [studentSyncResult, setStudentSyncResult] = useState(null);
  const [studentSyncError, setStudentSyncError] = useState(null);

  useEffect(() => {
    if (selectedClass) {
      // Extract tool settings from the backend structure
      const tools = selectedClass.tools || {};
      const documentEditorEnabled = tools.document_editor?.enabled ?? true;
      const erdEditorEnabled = tools.erd_editor?.enabled ?? true;
      
      const newSettings = {
        documentEditorEnabled,
        erdEditorEnabled,
        className: selectedClass.class_name || '',
        classCode: selectedClass.class_code || '',
        description: selectedClass.description || ''
      };
      setSettings(newSettings);
      setEditedSettings(newSettings);
    }
  }, [selectedClass]);

  const handleEditStart = () => {
    setEditedSettings({ ...settings });
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setEditedSettings({ ...settings });
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const response = await fetch(`${apiBaseUrl}/classes/${selectedClass.class_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        },
        credentials: 'include',
        body: JSON.stringify(editedSettings)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSettings({ ...editedSettings });
          setIsEditing(false);
          setSuccessMessage('Class settings updated successfully!');
          // Auto-hide success message after 3 seconds
          setTimeout(() => setSuccessMessage(null), 3000);
        } else {
          throw new Error(data.error || 'Failed to update class settings');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to update class settings`);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setError(`Error updating class settings: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleFeature = (feature) => {
    setEditedSettings(prev => ({
      ...prev,
      [feature]: !prev[feature]
    }));
  };

  // Canvas Integration Functions
  useEffect(() => {
    checkCanvasStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass]);

  const checkCanvasStatus = async () => {
    if (!selectedClass) return;

    try {
      const credResponse = await classApi.getCanvasCredentials();
      setHasCanvasCredentials(credResponse.has_credentials);
      if (credResponse.has_credentials) {
        setCanvasUrl(credResponse.canvas_url);
      }

      const integration = selectedClass.canvas_integration;
      setCanvasIntegration(integration);
    } catch (err) {
      console.error('Error checking Canvas status:', err);
    }
  };

  const handleCanvasSetup = () => {
    setShowCanvasModal(true);
    setCanvasStep(hasCanvasCredentials ? 2 : 1);
    if (hasCanvasCredentials && canvasStep === 2) {
      fetchCanvasCourses();
    }
  };

  const handleSaveCanvasCredentials = async (e) => {
    e.preventDefault();
    setCanvasLoading(true);
    setCanvasError(null);

    try {
      await classApi.saveCanvasCredentials(canvasUrl, accessToken);
      setHasCanvasCredentials(true);
      setSuccessMessage('Canvas credentials saved successfully!');

      setTimeout(() => {
        setCanvasStep(2);
        fetchCanvasCourses();
      }, 1500);
    } catch (err) {
      setCanvasError(err.message || 'Failed to save Canvas credentials');
    } finally {
      setCanvasLoading(false);
    }
  };

  const fetchCanvasCourses = async () => {
    setCanvasLoading(true);
    setCanvasError(null);

    try {
      const response = await classApi.getCanvasCourses();
      setCanvasCourses(response.courses || []);

      if (response.courses.length === 0) {
        setCanvasError('No courses found. Make sure you have teaching access in Canvas.');
      }
    } catch (err) {
      setCanvasError(err.message || 'Failed to fetch Canvas courses');
    } finally {
      setCanvasLoading(false);
    }
  };

  const handleLinkCanvasCourse = async () => {
    if (!selectedCanvasCourse) {
      setCanvasError('Please select a Canvas course');
      return;
    }

    setCanvasLoading(true);
    setCanvasError(null);

    try {
      await classApi.linkCanvasCourse(
        selectedClass.class_id,
        selectedCanvasCourse.id,
        selectedCanvasCourse.name
      );

      setSuccessMessage(`Successfully linked to Canvas course: ${selectedCanvasCourse.name}`);

      setTimeout(() => {
        setShowCanvasModal(false);
        checkCanvasStatus();
      }, 2000);
    } catch (err) {
      setCanvasError(err.message || 'Failed to link Canvas course');
    } finally {
      setCanvasLoading(false);
    }
  };

  // Export/Import Functions
  const handleExportClassData = async () => {
    if (!selectedClass) return;

    setExportLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiBaseUrl}/classes/${selectedClass.class_id}/export`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to export class data');
      }

      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedClass.class_code}_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);

      setSuccessMessage('Class data exported successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(`Failed to export class data: ${err.message}`);
    } finally {
      setExportLoading(false);
    }
  };

  const handleImportClassData = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedClass) return;
    e.target.value = '';

    setImportLoading(true);
    setError(null);

    try {
      const text = await file.text();
      const importData = JSON.parse(text);

      const response = await fetch(`${apiBaseUrl}/classes/${selectedClass.class_id}/import?mode=merge&include=all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        },
        credentials: 'include',
        body: JSON.stringify(importData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to import class data');
      }

      const result = await response.json();
      setSuccessMessage(`Class data imported successfully! ${JSON.stringify(result.imported_counts)}`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError(`Failed to import class data: ${err.message}`);
    } finally {
      setImportLoading(false);
    }
  };

  // Migration Wizard Functions
  const handleStartMigration = () => {
    setShowMigrationWizard(true);
    setMigrationStep(1);
    setMigrationError(null);
    setMigrationPhase(null);
    setNewClassName('');
    setNewClassCode('');
    setNewSemester('');
    setNewYear(new Date().getFullYear().toString());
    setSelectedItems({
      environments: true,
      assignments: true,
      rubrics: true
    });
    setFilterOptions({
      publishedOnly: false,
      excludeArchived: true
    });
  };

  // Smart dependency handling for migration selections
  const handleToggleSelection = (key) => {
    setSelectedItems(prev => {
      const newState = { ...prev };

      if (key === 'migrateAll') {
        // Toggle all content items (NOT enrollments - that's independent)
        const newValue = !(prev.environments && prev.assignments && prev.documents && prev.rubrics);
        newState.environments = newValue;
        newState.assignments = newValue;
        newState.documents = newValue;
        newState.rubrics = newValue;
        // Note: enrollments is NOT affected
      } else if (key === 'enrollments') {
        // Enrollments toggle independently
        newState.enrollments = !prev.enrollments;
      } else {
        // For other items, toggle independently
        newState[key] = !prev[key];
      }

      return newState;
    });
  };

  // Check if all content items are selected (for "migrate all" checkbox state)
  const allContentSelected = selectedItems.environments && selectedItems.assignments &&
                             selectedItems.documents && selectedItems.rubrics;

  const handleCreateAndMigrate = async () => {
    if (!newClassName.trim() || !newClassCode.trim() || !newSemester.trim() || !newYear.trim()) {
      setMigrationError('Please enter class name, code, semester, and year');
      return;
    }

    setMigrationLoading(true);
    setMigrationError(null);
    setMigrationPhase('export');

    let newClassId = null;

    try {
      // Step 1: Export data from current class FIRST (before creating new class)
      // This ensures we don't create an empty class if export fails
      const exportResponse = await fetch(`${apiBaseUrl}/classes/${selectedClass.class_id}/export`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        },
        credentials: 'include'
      });

      if (!exportResponse.ok) {
        const errorData = await exportResponse.json();
        throw new Error(errorData.error || 'Failed to export class data');
      }

      const exportData = await exportResponse.json();

      // Step 2: Apply filters and prepare data
      let filteredData;

      if (copyAllExceptStudentData) {
        // "Copy everything except student data" mode: skip the per-item filtering
        // below entirely and hand the whole export (including the full
        // collection-by-collection dump) to the backend, which does the
        // exclusion of student-generated collections itself.
        filteredData = exportData;
      } else {
        let filteredAssignments = exportData.assignments || [];
        let filteredDocuments = [];
        let filteredSections = [];

        // Filter assignments if selected
        if (selectedItems.assignments) {
          if (filterOptions.publishedOnly) {
            filteredAssignments = filteredAssignments.filter(a => a.is_published);
          }
          if (filterOptions.excludeArchived) {
            filteredAssignments = filteredAssignments.filter(a => !a.is_active || a.is_active === true);
          }
        } else {
          filteredAssignments = [];
        }

        // Handle documents and sections separately now
        if (selectedItems.documents || selectedItems.assignments) {
          const allDocuments = exportData.documents || [];
          const allSections = exportData.sections || [];

          // Get assignment-linked items if assignments are selected
          let assignmentLinkedDocuments = [];
          let assignmentLinkedSections = [];

          if (selectedItems.assignments) {
            const assignmentIds = new Set(filteredAssignments.map(a => a.assignment_id));
            assignmentLinkedDocuments = allDocuments.filter(d => d.assignment_id && assignmentIds.has(d.assignment_id));
            assignmentLinkedSections = allSections.filter(s => s.assignment_id && assignmentIds.has(s.assignment_id));
          }

          // Get standalone items if includeStandalone is enabled
          let standaloneDocuments = [];
          let standaloneSections = [];

          if (filterOptions.includeStandalone && selectedItems.documents) {
            standaloneDocuments = allDocuments.filter(d => !d.assignment_id);
            standaloneSections = allSections.filter(s => !s.assignment_id);
          }

          // Combine both types (use Set to avoid duplicates)
          const documentIds = new Set();
          [...assignmentLinkedDocuments, ...standaloneDocuments].forEach(d => {
            if (d.document_id && !documentIds.has(d.document_id)) {
              documentIds.add(d.document_id);
              filteredDocuments.push(d);
            }
          });

          const sectionIds = new Set();
          [...assignmentLinkedSections, ...standaloneSections].forEach(s => {
            if (s.section_id && !sectionIds.has(s.section_id)) {
              sectionIds.add(s.section_id);
              filteredSections.push(s);
            }
          });
        }

        // Filter environments if needed
        let filteredEnvironments = selectedItems.environments ? (exportData.environments || []) : [];
        if (selectedItems.environments && filterOptions.excludeArchived) {
          filteredEnvironments = filteredEnvironments.filter(e => !e.archived);
        }

        // Filter rubrics
        const filteredRubrics = selectedItems.rubrics ? (exportData.rubrics || []) : [];

        filteredData = {
          ...exportData,
          environments: filteredEnvironments,
          assignments: filteredAssignments,
          documents: filteredDocuments,
          sections: filteredSections,
          rubrics: filteredRubrics
        };
      }

      // Step 3: NOW create the new class (only after successful export)
      setMigrationPhase('create');
      const createResponse = await fetch(`${apiBaseUrl}/classes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        },
        credentials: 'include',
        body: JSON.stringify({
          class_name: newClassName,
          class_code: newClassCode,
          semester: newSemester,
          year: parseInt(newYear),
          description: `Migrated from ${selectedClass.class_code}`,
          // The Migration Wizard's whole point is copying Mongo data between
          // classes - it shouldn't require a running MinIO server to do that.
          // This does NOT affect the regular "New Class" flow, which still
          // creates a file storage bucket as normal.
          skip_minio: true
        })
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        throw new Error(errorData.error || 'Failed to create new class');
      }

      const newClass = await createResponse.json();
      // Backend returns: { success: true, class: { class_id: ..., ... } }
      newClassId = newClass.class?.class_id;

      if (!newClassId) {
        throw new Error('Failed to get class_id from created class');
      }

      // Step 4: Import selected data into new class
      // Include documents and sections when either assignments OR documents are selected
      const includeList = [];
      if (copyAllExceptStudentData) {
        // Course-content collections only (environments, assignments, documents,
        // sections, rubrics, plus every other non-student collection like ERD
        // requirements and attachments); enrollments are intentionally excluded.
        includeList.push('all_except_student_data');
      } else {
        if (selectedItems.environments) includeList.push('environments');
        if (selectedItems.assignments) includeList.push('assignments');
        if (selectedItems.documents || selectedItems.assignments) {
          includeList.push('documents');
          includeList.push('sections');
        }
        if (selectedItems.rubrics) includeList.push('rubrics');
        if (selectedItems.enrollments) includeList.push('enrollments');
      }

      setMigrationPhase('import');
      const importResponse = await fetch(
        `${apiBaseUrl}/classes/${newClassId}/import?mode=merge&include=${includeList.join(',')}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'User-Role': userRole,
            'Requesting-User': username
          },
          credentials: 'include',
          body: JSON.stringify(filteredData)
        }
      );

      if (!importResponse.ok) {
        const errorData = await importResponse.json();
        throw new Error(errorData.error || 'Failed to import data into new class');
      }

      const importResult = await importResponse.json();
      setMigrationPhase('done');
      setSuccessMessage(
        `Successfully created new class "${newClassName}" and migrated data! ${JSON.stringify(importResult.imported_counts)}`
      );

      setTimeout(() => {
        setShowMigrationWizard(false);
        setSuccessMessage(null);
        setMigrationPhase(null);
      }, 5000);
    } catch (err) {
      setMigrationPhase(null);
      // If class was created but migration failed, try to hard delete it (rollback)
      if (newClassId) {
        try {
          // Use hard-delete endpoint to completely remove the failed class
          const deleteResponse = await fetch(`${apiBaseUrl}/classes/${newClassId}/hard-delete`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'User-Role': userRole,
              'Requesting-User': username
            },
            credentials: 'include'
          });

          if (deleteResponse.ok) {
            setMigrationError(
              `Migration failed: ${err.message}\n\nThe incomplete class "${newClassName}" was automatically removed (rolled back).`
            );
          } else {
            const deleteError = await deleteResponse.json();
            setMigrationError(
              `Migration failed: ${err.message}\n\nWARNING: Failed to rollback the created class "${newClassName}". Error: ${deleteError.error || 'Unknown error'}. You may need to manually delete it.`
            );
          }
        } catch (deleteErr) {
          setMigrationError(
            `Migration failed: ${err.message}\n\nWARNING: Failed to rollback the created class "${newClassName}". You may need to manually delete it.`
          );
        }
      } else {
        // Class wasn't created yet, just show the error
        setMigrationError(`Migration failed: ${err.message}`);
      }
    } finally {
      setMigrationLoading(false);
    }
  };

  if (userRole !== 'Instructor' && userRole !== 'TA' && userRole !== 'Admin') {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold mb-4 text-red-600">Access Denied</h1>
        <p className="text-gray-600">This tool is only available to instructors, TAs, and administrators.</p>
      </div>
    );
  }

  if (!selectedClass) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold mb-4 text-gray-600">No Class Selected</h1>
        <p className="text-gray-600">Please select a class to manage its settings.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <MdSettings className="text-blue-600" size={24} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Class Settings</h1>
                <p className="text-gray-600">Configure class features and information</p>
              </div>
            </div>
            {!isEditing ? (
              <button
                onClick={handleEditStart}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <MdEdit size={16} />
                <span>Edit Settings</span>
              </button>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={handleEditCancel}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <MdCancel size={16} />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <MdSave size={16} />
                  <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <MdInfo className="text-green-600 mr-2" />
            <span className="text-green-800 font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <MdInfo className="text-red-600 mr-2" />
              <span className="text-red-800 font-medium">{error}</span>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-600 hover:text-red-800"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Class Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Class Information</h2>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Class Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedSettings.className}
                  onChange={(e) => handleInputChange('className', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900 py-2">{settings.className}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Class Code
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedSettings.classCode}
                  onChange={(e) => handleInputChange('classCode', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900 py-2">{settings.classCode}</p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            {isEditing ? (
              <textarea
                value={editedSettings.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter class description..."
              />
            ) : (
              <p className="text-gray-900 py-2">{settings.description || 'No description provided'}</p>
            )}
          </div>
        </div>
      </div>

      {/* Feature Settings */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Feature Settings</h2>
          <p className="text-gray-600 text-sm mt-1">Enable or disable features available to students in this class</p>
        </div>
        <div className="px-6 py-4 space-y-4">
          {/* Document Editor Feature */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <MdDescription className="text-purple-600" size={20} />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">Document Editor</h3>
                <p className="text-xs text-gray-600">AI-powered document creation and editing tool</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {isEditing ? (
                <button
                  onClick={() => toggleFeature('documentEditorEnabled')}
                  className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    editedSettings.documentEditorEnabled
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-red-100 text-red-800 hover:bg-red-200'
                  }`}
                >
                  {editedSettings.documentEditorEnabled ? (
                    <>
                      <MdVisibility size={14} />
                      <span>Enabled</span>
                    </>
                  ) : (
                    <>
                      <MdVisibilityOff size={14} />
                      <span>Disabled</span>
                    </>
                  )}
                </button>
              ) : (
                <span className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${
                  settings.documentEditorEnabled
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {settings.documentEditorEnabled ? (
                    <>
                      <MdVisibility size={14} />
                      <span>Enabled</span>
                    </>
                  ) : (
                    <>
                      <MdVisibilityOff size={14} />
                      <span>Disabled</span>
                    </>
                  )}
                </span>
              )}
            </div>
          </div>

          {/* ERD Editor Feature */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <MdEdit className="text-blue-600" size={20} />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">ERD Editor</h3>
                <p className="text-xs text-gray-600">Entity Relationship Diagram creation and editing tool</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {isEditing ? (
                <button
                  onClick={() => toggleFeature('erdEditorEnabled')}
                  className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    editedSettings.erdEditorEnabled
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-red-100 text-red-800 hover:bg-red-200'
                  }`}
                >
                  {editedSettings.erdEditorEnabled ? (
                    <>
                      <MdVisibility size={14} />
                      <span>Enabled</span>
                    </>
                  ) : (
                    <>
                      <MdVisibilityOff size={14} />
                      <span>Disabled</span>
                    </>
                  )}
                </button>
              ) : (
                <span className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${
                  settings.erdEditorEnabled
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {settings.erdEditorEnabled ? (
                    <>
                      <MdVisibility size={14} />
                      <span>Enabled</span>
                    </>
                  ) : (
                    <>
                      <MdVisibilityOff size={14} />
                      <span>Disabled</span>
                    </>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Info Note */}
        <div className="px-6 py-4 border-t border-gray-200 bg-blue-50">
          <div className="flex items-start space-x-2">
            <MdInfo className="text-blue-600 mt-0.5" size={16} />
            <div className="text-xs text-blue-800">
              <p><strong>Note:</strong> Changes to feature settings will affect all students in this class. Disabled features will not appear in the navigation menu for students.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Class Data Management Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Class Data Management</h2>
          <p className="text-gray-600 text-sm mt-1">Export, import, or migrate class data to another class</p>
        </div>
        <div className="px-6 py-4 space-y-4">
          {/* Export/Import Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Export */}
            <div className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-start space-x-3 mb-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MdDownload className="text-green-600" size={20} />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-gray-900">Export Class Data</h3>
                  <p className="text-xs text-gray-600 mt-1">
                    Download all class data (environments, assignments, documents, rubrics) as a JSON file
                  </p>
                </div>
              </div>
              <button
                onClick={handleExportClassData}
                disabled={exportLoading}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                <MdDownload size={16} />
                <span>{exportLoading ? 'Exporting...' : 'Export Data'}</span>
              </button>
            </div>

            {/* Import */}
            <div className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-start space-x-3 mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MdUpload className="text-blue-600" size={20} />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-gray-900">Import Class Data</h3>
                  <p className="text-xs text-gray-600 mt-1">
                    Upload a JSON file to import data into this class (merges with existing data)
                  </p>
                </div>
              </div>
              <label className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer">
                <MdUpload size={16} />
                <span>{importLoading ? 'Importing...' : 'Import Data'}</span>
                <input
                  ref={importFileRef}
                  type="file"
                  accept=".json"
                  onChange={handleImportClassData}
                  disabled={importLoading}
                  className="sr-only"
                />
              </label>
            </div>
          </div>

          {/* Migration Wizard */}
          <div className="p-4 border-2 border-purple-200 bg-purple-50 rounded-lg">
            <div className="flex items-start space-x-3 mb-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <MdContentCopy className="text-purple-600" size={20} />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900">Migration Wizard</h3>
                <p className="text-xs text-gray-600 mt-1">
                  Create a new class and selectively migrate data from this class
                </p>
              </div>
            </div>
            <button
              onClick={handleStartMigration}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <MdContentCopy size={16} />
              <span>Start Migration Wizard</span>
            </button>
          </div>

          {/* Info Note */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
            <div className="flex items-start space-x-2">
              <MdInfo className="text-amber-600 mt-0.5 flex-shrink-0" size={16} />
              <div className="text-xs text-amber-800">
                <p><strong>Important:</strong> Export/Import operations include environments, assignments, documents, sections, and rubrics. Student submissions and grades are NOT included. Use the Migration Wizard to create a new class with selected data.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Canvas Integration Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Canvas LMS Integration</h2>
          <p className="text-gray-600 text-sm mt-1">Connect this class to Canvas for grade syncing</p>
        </div>
        <div className="px-6 py-4">
          {canvasIntegration?.enabled ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-green-900">Connected to Canvas</p>
                  <p className="text-xs text-green-700 mt-1">
                    {canvasIntegration.canvas_course_name}
                  </p>
                  {canvasIntegration.last_synced && (
                    <p className="text-xs text-green-600 mt-1">
                      Last synced: {new Date(canvasIntegration.last_synced).toLocaleString()}
                    </p>
                  )}
                </div>
                <button
                  onClick={handleCanvasSetup}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Change Course
                </button>
              </div>

              {/* Sync Students button */}
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div>
                  <p className="text-sm font-medium text-blue-900">Sync Students from Canvas</p>
                  <p className="text-xs text-blue-700 mt-1">
                    Import enrolled students from Canvas. New users are auto-created with a UIC email account.
                  </p>
                </div>
                <button
                  onClick={async () => {
                    setStudentSyncError(null);
                    setStudentSyncResult(null);
                    setStudentPreview(null);
                    setShowStudentSync(true);
                    setStudentSyncLoading(true);
                    try {
                      const res = await classApi.previewCanvasStudents(selectedClass.class_id);
                      if (res.success) setStudentPreview(res.students);
                      else setStudentSyncError(res.error || 'Failed to load preview');
                    } catch {
                      setStudentSyncError('Failed to load preview');
                    } finally {
                      setStudentSyncLoading(false);
                    }
                  }}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 whitespace-nowrap"
                >
                  Sync Students
                </button>
              </div>

              {/* Student Sync Modal */}
              {showStudentSync && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                  <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
                    <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">Sync Students from Canvas</h3>
                      <button onClick={() => setShowStudentSync(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-6 py-4">
                      {studentSyncLoading && (
                        <div className="flex items-center justify-center py-12">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3" />
                          <span className="text-gray-600">Loading Canvas students…</span>
                        </div>
                      )}

                      {studentSyncError && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                          {studentSyncError}
                        </div>
                      )}

                      {studentSyncResult && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-1">
                          <p className="text-sm font-semibold text-green-900">Sync complete</p>
                          <p className="text-xs text-green-800">Created: {studentSyncResult.created}</p>
                          <p className="text-xs text-green-800">Enrolled: {studentSyncResult.enrolled}</p>
                          <p className="text-xs text-green-800">Already enrolled: {studentSyncResult.already_enrolled}</p>
                          {studentSyncResult.failed > 0 && (
                            <p className="text-xs text-red-700">Failed: {studentSyncResult.failed}</p>
                          )}
                        </div>
                      )}

                      {studentPreview && !studentSyncResult && (
                        <div className="space-y-2">
                          <p className="text-sm text-gray-600 mb-3">
                            {studentPreview.length} student{studentPreview.length !== 1 ? 's' : ''} found in Canvas.
                          </p>
                          {studentPreview.map((s, i) => (
                            <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                              <div>
                                <p className="text-sm font-medium text-gray-800">{s.canvas_name}</p>
                                <p className="text-xs text-gray-500">{s.username || s.error}</p>
                              </div>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                s.action === 'none' ? 'bg-gray-100 text-gray-600' :
                                s.action === 'enroll' ? 'bg-blue-100 text-blue-700' :
                                s.action === 'create_and_enroll' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {s.action === 'none' ? 'Already enrolled' :
                                 s.action === 'enroll' ? 'Will enroll' :
                                 s.action === 'create_and_enroll' ? 'Will create + enroll' : 'Error'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {!studentSyncResult && (
                      <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                        <button
                          onClick={() => setShowStudentSync(false)}
                          className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          disabled={!studentPreview || studentSyncLoading}
                          onClick={async () => {
                            setStudentSyncLoading(true);
                            setStudentSyncError(null);
                            try {
                              const res = await classApi.syncStudentsFromCanvas(selectedClass.class_id);
                              if (res.success) {
                                setStudentSyncResult(res.summary);
                                setStudentPreview(null);
                              } else {
                                setStudentSyncError(res.error || 'Sync failed');
                              }
                            } catch {
                              setStudentSyncError('Sync failed');
                            } finally {
                              setStudentSyncLoading(false);
                            }
                          }}
                          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {studentSyncLoading ? 'Syncing…' : 'Confirm Sync'}
                        </button>
                      </div>
                    )}
                    {studentSyncResult && (
                      <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                        <button
                          onClick={() => setShowStudentSync(false)}
                          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Done
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-gray-600 mb-4">
                {hasCanvasCredentials
                  ? 'Link this class to a Canvas course to enable grade syncing'
                  : 'Set up Canvas integration to sync grades to your Canvas courses'
                }
              </p>
              <button
                onClick={handleCanvasSetup}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {hasCanvasCredentials ? 'Link Canvas Course' : 'Setup Canvas Integration'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Canvas Setup Modal */}
      {showCanvasModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-semibold">Canvas Integration Setup</h3>
              <button
                onClick={() => setShowCanvasModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="px-6 py-4">
              {canvasStep === 1 && (
                <div>
                  <h4 className="text-lg font-medium mb-4">Step 1: Connect Your Canvas Account</h4>
                  <p className="text-gray-600 mb-6">Enter your Canvas institution URL and access token.</p>

                  <form onSubmit={handleSaveCanvasCredentials}>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Canvas URL
                        </label>
                        <input
                          type="text"
                          placeholder="https://canvas.instructure.com"
                          value={canvasUrl}
                          onChange={(e) => setCanvasUrl(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">Your institution's Canvas URL</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Access Token
                        </label>
                        <input
                          type="password"
                          placeholder="Enter your Canvas access token"
                          value={accessToken}
                          onChange={(e) => setAccessToken(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Generate in Canvas: Account → Settings → New Access Token
                        </p>
                      </div>

                      {canvasError && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                          <p className="text-sm text-red-800">{canvasError}</p>
                        </div>
                      )}

                      <div className="flex justify-end space-x-3 pt-4">
                        <button
                          type="button"
                          onClick={() => setShowCanvasModal(false)}
                          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={canvasLoading}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                          {canvasLoading ? 'Validating...' : 'Save & Continue'}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              )}

              {canvasStep === 2 && (
                <div>
                  <h4 className="text-lg font-medium mb-4">Step 2: Select Canvas Course</h4>
                  <p className="text-gray-600 mb-6">Choose which Canvas course to link with this class.</p>

                  {canvasLoading ? (
                    <div className="text-center py-8">
                      <p className="text-gray-600">Loading courses...</p>
                    </div>
                  ) : canvasCourses.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto mb-4">
                      {canvasCourses.map((course) => (
                        <div
                          key={course.id}
                          onClick={() => setSelectedCanvasCourse(course)}
                          className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                            selectedCanvasCourse?.id === course.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <p className="font-medium text-gray-900">{course.name}</p>
                          <p className="text-sm text-gray-600">{course.course_code}</p>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {canvasError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md mb-4">
                      <p className="text-sm text-red-800">{canvasError}</p>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      onClick={() => setCanvasStep(1)}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleLinkCanvasCourse}
                      disabled={canvasLoading || !selectedCanvasCourse}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {canvasLoading ? 'Linking...' : 'Link Course'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Migration Wizard Modal */}
      {showMigrationWizard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-semibold">Migration Wizard</h3>
              <button
                onClick={() => setShowMigrationWizard(false)}
                disabled={migrationLoading}
                className="text-gray-400 hover:text-gray-600 text-2xl disabled:opacity-30 disabled:cursor-not-allowed"
              >
                ×
              </button>
            </div>

            {/* Migration progress bar - only shown while create+migrate is running */}
            {migrationPhase && (() => {
              const PHASES = [
                { key: 'export', label: 'Exporting class data', percent: 25 },
                { key: 'create', label: 'Creating new class', percent: 55 },
                { key: 'import', label: 'Importing data', percent: 85 },
                { key: 'done', label: 'Migration complete', percent: 100 }
              ];
              const activeIndex = PHASES.findIndex(p => p.key === migrationPhase);
              const activePhase = PHASES[activeIndex] || PHASES[0];

              return (
                <div className="px-6 pt-4">
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-sm font-medium text-gray-700">{activePhase.label}{activePhase.key !== 'done' ? '…' : ''}</span>
                    <span className="text-xs text-gray-500">{activePhase.percent}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ease-out ${activePhase.key === 'done' ? 'bg-green-500' : 'bg-purple-600'}`}
                      style={{ width: `${activePhase.percent}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1.5">
                    {PHASES.map((phase, idx) => (
                      <span
                        key={phase.key}
                        className={`text-[11px] ${idx <= activeIndex ? 'text-purple-700 font-medium' : 'text-gray-400'}`}
                      >
                        {phase.label.split(' ')[0]}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div className="px-6 py-4">
              {migrationStep === 1 && (
                <div>
                  <h4 className="text-lg font-medium mb-4">Create New Class</h4>
                  <p className="text-gray-600 mb-6">
                    Enter details for the new class. Data from <strong>{selectedClass?.class_name}</strong> will be copied to this new class.
                  </p>

                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        New Class Name
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., Database Design Spring 2026"
                        value={newClassName}
                        onChange={(e) => setNewClassName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        New Class Code
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., CS340-SP26"
                        value={newClassCode}
                        onChange={(e) => setNewClassCode(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Semester
                        </label>
                        <select
                          value={newSemester}
                          onChange={(e) => setNewSemester(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">Select semester</option>
                          <option value="Spring">Spring</option>
                          <option value="Summer">Summer</option>
                          <option value="Fall">Fall</option>
                          <option value="Winter">Winter</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Year
                        </label>
                        <input
                          type="number"
                          placeholder="e.g., 2026"
                          value={newYear}
                          onChange={(e) => setNewYear(e.target.value)}
                          min="2000"
                          max="2100"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Copy everything except student data - shortcut mode */}
                  <div className="mb-6">
                    <label className="flex items-start space-x-3 p-3 border-2 border-emerald-300 bg-emerald-50 rounded-lg hover:bg-emerald-100 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={copyAllExceptStudentData}
                        onChange={() => setCopyAllExceptStudentData(prev => !prev)}
                        className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 mt-1"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-semibold text-emerald-900">Copy Everything Except Student Data</span>
                        <p className="text-xs text-emerald-700 mt-0.5">
                          Copies all course content — environments, assignments, documents, sections, rubrics, ERD requirements,
                          attachments, instructor-pinned document questions, and anything else that isn't student-generated.
                          Excludes submissions, grades, feedback, discussions, saved work, activity logs, and enrollments.
                          Overrides the options below.
                        </p>
                      </div>
                    </label>
                  </div>

                  <fieldset
                    disabled={copyAllExceptStudentData}
                    className={copyAllExceptStudentData ? 'opacity-40 pointer-events-none' : ''}
                  >
                  <div className="mb-6">
                    <h5 className="text-sm font-medium text-gray-700 mb-3">Select what to migrate:</h5>
                    <div className="space-y-2">
                      {/* Migrate All - First checkbox */}
                      <label className="flex items-start space-x-3 p-3 border-2 border-purple-300 bg-purple-50 rounded-lg hover:bg-purple-100 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={allContentSelected}
                          onChange={() => handleToggleSelection('migrateAll')}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 mt-1"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-semibold text-purple-900">Migrate All Class Data</span>
                          <p className="text-xs text-purple-700 mt-0.5">Select all content types below (excludes enrollments)</p>
                        </div>
                      </label>

                      {/* Individual content type checkboxes */}
                      {[
                        { key: 'environments', label: 'Environments', description: 'Database environments, requirements & ERD data' },
                        { key: 'assignments', label: 'Assignments', description: 'All assignments and their linked documents & sections' },
                        { key: 'documents', label: 'Standalone Documents & Sections', description: 'Documents and sections NOT linked to assignments' },
                        { key: 'rubrics', label: 'Rubrics', description: 'Grading rubrics (assignment-specific rubrics included with assignments)' }
                      ].map(({ key, label, description }) => (
                        <label key={key} className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedItems[key]}
                            onChange={() => handleToggleSelection(key)}
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 mt-1"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-900">{label}</span>
                            <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Filter Options */}
                  <div className="mb-6">
                    <h5 className="text-sm font-medium text-gray-700 mb-3">Filter options:</h5>
                    <div className="space-y-2">
                      <label className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filterOptions.publishedOnly}
                          onChange={(e) => setFilterOptions(prev => ({ ...prev, publishedOnly: e.target.checked }))}
                          disabled={!selectedItems.assignments}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 mt-1 disabled:opacity-50"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">Published assignments only</span>
                          <p className="text-xs text-gray-500 mt-0.5">Only migrate assignments that are published to students</p>
                        </div>
                      </label>

                      <label className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filterOptions.excludeArchived}
                          onChange={(e) => setFilterOptions(prev => ({ ...prev, excludeArchived: e.target.checked }))}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 mt-1"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">Exclude archived content</span>
                          <p className="text-xs text-gray-500 mt-0.5">Skip archived assignments and environments</p>
                        </div>
                      </label>

                      <label className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filterOptions.includeStandalone}
                          onChange={(e) => setFilterOptions(prev => ({ ...prev, includeStandalone: e.target.checked }))}
                          disabled={!selectedItems.documents}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 mt-1 disabled:opacity-50"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">Include standalone content</span>
                          <p className="text-xs text-gray-500 mt-0.5">Include documents, sections, and ERD data not linked to assignments</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Enrollments Section - Separate and Independent */}
                  <div className="mb-6">
                    <h5 className="text-sm font-medium text-gray-700 mb-3">User Enrollments:</h5>
                    <div className="space-y-2">
                      <label className="flex items-start space-x-3 p-3 border-2 border-blue-300 bg-blue-50 rounded-lg hover:bg-blue-100 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedItems.enrollments}
                          onChange={() => handleToggleSelection('enrollments')}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-1"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-semibold text-blue-900">Copy User Enrollments</span>
                          <p className="text-xs text-blue-700 mt-0.5">
                            Copy all student, TA, and instructor enrollments to the new class (does not include submissions or grades)
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                  </fieldset>

                  {/* Info Note about Migration */}
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md mb-4">
                    <div className="flex items-start space-x-2">
                      <MdInfo className="text-blue-600 mt-0.5 flex-shrink-0" size={16} />
                      <div className="text-xs text-blue-800">
                        <p><strong>Migration Notes:</strong></p>
                        <ul className="list-disc ml-4 mt-1 space-y-1">
                          <li>Enrollments copy user roles only (no grades/submissions)</li>
                          <li>Users must exist in the system to be enrolled</li>
                          <li>Standalone content includes documents, sections, and ERD data not linked to assignments</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {migrationError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md mb-4">
                      <p className="text-sm text-red-800">{migrationError}</p>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      onClick={() => setShowMigrationWizard(false)}
                      disabled={migrationLoading}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateAndMigrate}
                      disabled={migrationLoading || !newClassName.trim() || !newClassCode.trim() || !newSemester.trim() || !newYear.trim()}
                      className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
                    >
                      {migrationLoading ? 'Creating & Migrating...' : 'Create Class & Migrate Data'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassSettings;